/* eslint-disable import/max-dependencies -- composes the shift fields, invite state, and every invite-related modal */
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { BoostIncreaseRequestModal } from "@src/appV2/redesign/SpendingControls/components/SpendingLimitIncreaseRequestModal/BoostIncreaseRequestModal";
import { ModalStatus, useModalState } from "@src/lib/ui-react";
import { useCallback, useEffect, useRef } from "react";

import { InviteFooter } from "../../../AddShiftPanel/components/InviteFooter";
import { ProfessionalSearchField } from "../../../AddShiftPanel/components/ProfessionalSearchField";
import { RoleSelectField } from "../../../AddShiftPanel/components/RoleSelectField";
import { ShiftDateField } from "../../../AddShiftPanel/components/ShiftDateField";
import { ShiftTypeToggle } from "../../../AddShiftPanel/components/ShiftTypeToggle";
import { type AddShiftQualificationOption } from "../../../AddShiftPanel/types";
import { type SelectOption } from "../../../components/Select";
import { EditShiftTimeDialog } from "../EditShiftTimeModal/EditShiftTimeDialog";
import { useSaveEditedShiftTime } from "../EditShiftTimeModal/useSaveEditedShiftTime";
import { ShiftPostingHeader } from "../ShiftPostingForm/ShiftPostingHeader";
import { useEditableShiftFields } from "./hooks/useEditableShiftFields";
import { useInviteModeState } from "./hooks/useInviteModeState";
import { InviteBlockedModal } from "./InviteBlockedModal";
import { InviteSelectedProfessional } from "./InviteSelectedProfessional";
import { InviteValidationResultsModal } from "./InviteValidationResultsModal";
import { PostAsOpenShiftInfoModal } from "./PostAsOpenShiftInfoModal";
import { type Professional, type ShiftDetails } from "./types";
/* eslint-enable import/max-dependencies */

interface EditedShiftTimes {
  startTime: Date;
  endTime: Date;
  qualification: string;
  timeSlot: string;
}

interface InviteContentProps {
  shiftDetails: ShiftDetails;
  /**
   * When provided, syncs time edits from an external source (e.g. PostingFormContext
   * in DailyView). The chat invite flow does not use this.
   */
  lastEditedTimes?: EditedShiftTimes;
  /**
   * When provided, the invite flow starts with this professional already selected
   * (e.g. when the invite is opened from a chat or My Professionals row).
   */
  preselectedProfessional?: Professional;
  /**
   * The shift's role/date/shift-type/time render as the same editable fields
   * the Add Shift panel uses, pre-filled from `shiftDetails` and changeable by
   * the user, so the Invite flow looks identical to the Add Shift panel's
   * Invite tab.
   */
  qualificationOptions: AddShiftQualificationOption[];
  /**
   * When true, dismissing the "Cannot send invites" warning also closes the
   * parent surface via `onClose`. Set on surfaces where the slot is fixed
   * (DailyView's invite drawer / bottom sheet) and the user can't recover
   * inline. Leave false (default) on surfaces where the user can pick a
   * different slot, like the chat invite dialog.
   */
  closeOnBlock?: boolean;
  /** Required when `closeOnBlock` is true. */
  onClose?: () => void;
  onSuccess: () => void;
}

/**
 * Unified invite content for surfaces where the shift already exists (Schedule
 * posting cards, Chat, My Professionals, Worker Profile, Turnover Management)
 * — editable role/date/type/time fields, plus the same professional-search/
 * bonus/footer pieces used by the Add Shift panel's Invite tab, via the shared
 * `useInviteModeState` hook.
 */
export function InviteContent(props: InviteContentProps) {
  const {
    shiftDetails,
    lastEditedTimes,
    preselectedProfessional,
    qualificationOptions,
    closeOnBlock = false,
    onClose,
    onSuccess,
  } = props;

  const { user } = useSession();
  const editTimeModalState = useModalState(ModalStatus.CLOSED);

  const editableFields = useEditableShiftFields({
    workplaceId: shiftDetails.workplaceId,
    timezone: shiftDetails.timezone,
    initialQualification: shiftDetails.qualification,
    initialDate: shiftDetails.selectedDate,
    initialTimeSlot: shiftDetails.timeSlot,
    initialStartTime: shiftDetails.startTime,
    initialEndTime: shiftDetails.endTime,
  });
  const { setStartTime, setEndTime } = editableFields;

  // Sync with externally edited times (e.g. from PostingFormContext in DailyView)
  useEffect(() => {
    if (
      lastEditedTimes &&
      lastEditedTimes.qualification === shiftDetails.qualification &&
      lastEditedTimes.timeSlot === shiftDetails.timeSlot
    ) {
      setStartTime(lastEditedTimes.startTime);
      setEndTime(lastEditedTimes.endTime);
    }
  }, [
    lastEditedTimes,
    shiftDetails.qualification,
    shiftDetails.timeSlot,
    setStartTime,
    setEndTime,
  ]);

  const editableQualificationDisplayName =
    qualificationOptions.find((option) => option.name === editableFields.qualification)
      ?.displayLabel ?? shiftDetails.qualificationDisplayName;

  const roleOptions: SelectOption[] = qualificationOptions.map((option) => ({
    value: option.name,
    label: option.displayLabel,
  }));

  const effectiveShiftDetails: ShiftDetails = {
    startTime: editableFields.startTime,
    endTime: editableFields.endTime,
    timeSlot: editableFields.timeSlot,
    qualification: editableFields.qualification,
    qualificationDisplayName: editableQualificationDisplayName,
    workplaceId: shiftDetails.workplaceId,
    timezone: shiftDetails.timezone,
    selectedDate: editableFields.date,
  };

  const {
    searchQuery,
    isSearching,
    allProfessionals,
    starredProfessionals,
    recentProfessionals,
    setSearchQuery,
    status,
    selectedProfessional,
    postToMarketplace,
    blockReason,
    canSendInvite,
    errorReasons,
    warningReasons,
    missingDocuments,
    errorDetail,
    startTimeError,
    errorModalState,
    blockedModalState,
    selectProfessional,
    clearSelection,
    setPostToMarketplace,
    sendInvite,
    dismissError,
    postToMarketplaceInfoModalState,
    isChargeRateVisible,
    isChargeRateLoading,
    hourlyBonusInMinorUnits,
    maxBoostInMinorUnits,
    professionalBonusDisplay,
    onBoostChange,
    isBoostOverLimit,
    boostOverageInMinorUnits,
    boostLimitInMinorUnits,
    isBoostIncreaseRequested,
    onRequestBoostIncrease,
    isAdmin,
    spendLimitBlocksSend,
    weekStartDate,
    increaseRequest,
  } = useInviteModeState({
    shiftDetails: effectiveShiftDetails,
    preselectedProfessional,
    onSuccess,
  });

  // Mirrors AddShiftPanelContainer's key-based remount of InviteModeController:
  // clear the stale professional/boost when the shift changes, unless a
  // professional is preselected (Chat etc.), where it must stay fixed.
  const inviteResetKey = `${editableFields.qualification}|${editableFields.date.toISOString()}|${editableFields.timeSlot}`;
  const previousInviteResetKeyRef = useRef(inviteResetKey);
  useEffect(() => {
    const hasResetKeyChanged = previousInviteResetKeyRef.current !== inviteResetKey;
    previousInviteResetKeyRef.current = inviteResetKey;
    if (hasResetKeyChanged && !isDefined(preselectedProfessional)) {
      clearSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clearSelection's identity churns every render; omitted to avoid running this on every render instead of only on real resetKey changes.
  }, [inviteResetKey, preselectedProfessional]);

  const handleBlockedModalClose = useCallback(() => {
    blockedModalState.closeModal();
    if (closeOnBlock) {
      onClose?.();
    }
  }, [blockedModalState, closeOnBlock, onClose]);

  const handleApplyEditedTimes = useCallback(
    (times: { start: Date; end: Date }) => {
      editableFields.setStartTime(times.start);
      editableFields.setEndTime(times.end);
    },
    [editableFields]
  );

  const handleEditTimeSave = useSaveEditedShiftTime({
    workplaceId: shiftDetails.workplaceId,
    workplaceUserId: user?._id,
    qualification: editableFields.qualification,
    timeSlot: editableFields.timeSlot,
    timezone: shiftDetails.timezone,
    onApplyTimes: handleApplyEditedTimes,
  });

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            padding: 5,
          }}
        >
          <Stack spacing={5}>
            <RoleSelectField
              options={roleOptions}
              value={editableFields.qualification}
              onChange={editableFields.setQualification}
            />
            <ShiftDateField
              timezone={shiftDetails.timezone}
              value={editableFields.date}
              onChange={editableFields.setDate}
            />
            <ShiftTypeToggle
              value={editableFields.timeSlot}
              onChange={editableFields.setTimeSlot}
            />
            <ShiftPostingHeader
              endTime={editableFields.endTime}
              qualificationDisplayName={editableQualificationDisplayName}
              startTime={editableFields.startTime}
              timeSlot={editableFields.timeSlot}
              timezone={shiftDetails.timezone}
              onEditTime={() => {
                editTimeModalState.openModal();
              }}
            />

            {isDefined(preselectedProfessional) ? (
              <InviteSelectedProfessional professional={preselectedProfessional} />
            ) : (
              <ProfessionalSearchField
                allProfessionals={allProfessionals}
                isSearching={isSearching}
                recentProfessionals={recentProfessionals}
                searchQuery={searchQuery}
                selectedProfessional={selectedProfessional}
                starredProfessionals={starredProfessionals}
                status={status}
                onSearchChange={setSearchQuery}
                onSelectProfessional={selectProfessional}
              />
            )}
          </Stack>
        </Box>

        <Box
          sx={(theme) => ({
            padding: theme.spacing(5),
            borderTop: `1px solid ${theme.palette.border?.base}`,
          })}
        >
          <InviteFooter
            boostLimitInMinorUnits={boostLimitInMinorUnits}
            boostOverageInMinorUnits={boostOverageInMinorUnits}
            canSend={canSendInvite && !spendLimitBlocksSend}
            errorMessage={startTimeError}
            hourlyBonusInMinorUnits={hourlyBonusInMinorUnits}
            isAdmin={isAdmin}
            isBoostIncreaseRequested={isBoostIncreaseRequested}
            isBoostOverLimit={isBoostOverLimit}
            isChargeRateLoading={isChargeRateLoading}
            isChargeRateVisible={isChargeRateVisible}
            isSending={status === "sending" || status === "validating"}
            maxBoostInMinorUnits={maxBoostInMinorUnits}
            postToMarketplace={postToMarketplace}
            professionalBonusDisplay={professionalBonusDisplay}
            onBoostChange={onBoostChange}
            onPostToMarketplaceChange={setPostToMarketplace}
            onPostToMarketplaceInfo={postToMarketplaceInfoModalState.openModal}
            onRequestBoostIncrease={onRequestBoostIncrease}
            onSend={sendInvite}
          />
        </Box>
      </Box>

      <PostAsOpenShiftInfoModal
        modalState={postToMarketplaceInfoModalState}
        onClose={postToMarketplaceInfoModalState.closeModal}
      />

      {blockReason && (
        <InviteBlockedModal
          blockReason={blockReason}
          modalState={blockedModalState}
          onClose={handleBlockedModalClose}
        />
      )}

      <InviteValidationResultsModal
        detail={errorDetail}
        errorReasons={errorReasons}
        missingDocuments={missingDocuments}
        modalState={errorModalState}
        professionalName={selectedProfessional?.name}
        qualificationName={effectiveShiftDetails.qualificationDisplayName}
        shiftStartDate={effectiveShiftDetails.startTime}
        warningReasons={warningReasons}
        workplaceId={shiftDetails.workplaceId}
        onDismiss={dismissError}
        onSelectAnother={isDefined(preselectedProfessional) ? undefined : clearSelection}
      />

      <EditShiftTimeDialog
        validatePastStartTime
        validateTimeslotBoundary
        initialTime={{ start: editableFields.startTime, end: editableFields.endTime }}
        slotDefault={editableFields.defaultTimes}
        modalState={editTimeModalState}
        qualificationName={editableQualificationDisplayName}
        showQualificationSpecificOption={editableFields.isQualificationSpecificEnabled}
        timeSlot={editableFields.timeSlot}
        timezone={shiftDetails.timezone}
        onSave={handleEditTimeSave}
      />

      <BoostIncreaseRequestModal
        increaseRequest={increaseRequest}
        weekStartDate={weekStartDate}
        workplaceId={shiftDetails.workplaceId}
      />
    </>
  );
}
